import { forwardRef, useEffect, useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { QRLabelBase, LabelRow, MaterialList } from "./QRLabelBase";
import { api } from "@/lib/apiClient";
import { Skeleton } from "@/components/ui/skeleton";
import type { LabelSize, PlotterCutLabelData } from "@/types/labels";

interface PlotterCut {
  id: string;
  package_code: string;
  package_name: string;
  materials: Array<{
    material_name: string;
    layers: number;
    gsm?: number;
  }>;
  primary_material?: string;
  total_layers: number;
  batch_number?: string;
  roll_id?: string;
  cut_date: string;
  weight_grams?: number;
  dimensions?: string;
  out_time_limit_minutes?: number;
  expiration_date?: string;
  operator_name?: string;
  created_by_name?: string;
}

interface PlotterCutLabelProps {
  plotterCut?: PlotterCut;
  plotterCutId?: string;
  shortCode?: string;
  size?: LabelSize;
}

export const PlotterCutLabel = forwardRef<HTMLDivElement, PlotterCutLabelProps>(
  ({ plotterCut: propPlotterCut, plotterCutId, shortCode, size = "medium" }, ref) => {
     
    const [plotterCut, setPlotterCut] = useState<PlotterCut | null>(propPlotterCut || null);
    const [loading, setLoading] = useState(!propPlotterCut && !!plotterCutId);
    const [labelCode, setLabelCode] = useState(shortCode || "");

    useEffect(() => {
      if (propPlotterCut) {
        setPlotterCut(propPlotterCut);
        return;
      }

      if (!plotterCutId) return;

      const fetchData = async () => {
        setLoading(true);
        try {
          const { data } = await api
            .from("plotter_cuts")
            .select("*, created_by:profiles!plotter_cuts_created_by_fkey(full_name)")
            .eq("id", plotterCutId)
            .single();

          if (data) {
            setPlotterCut({
              ...data,
              operator_name: data.created_by?.full_name || "N/A",
            });
          }

          // Get or create label
          if (!shortCode) {
            const { data: existingLabel } = await api
              .from("label_prints")
              .select("short_code")
              .eq("plotter_cut_id", plotterCutId)
              .eq("label_type", "plotter")
              .eq("is_active", true)
              .order("printed_at", { ascending: false })
              .limit(1)
              .single();

            if (existingLabel) {
              setLabelCode(existingLabel.short_code);
            }
          }
        } catch (err) {
          console.error("Error fetching plotter cut:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }, [plotterCutId, propPlotterCut, shortCode]);

    if (loading) {
      return (
        <div style={{ width: size === "small" ? "50mm" : size === "medium" ? "100mm" : "150mm" }}>
          <Skeleton className="h-20 w-full" />
        </div>
      );
    }

    if (!plotterCut) {
      return <div className="text-destructive">Error: Paquete no encontrado</div>;
    }

    // Build label data
    const labelData: PlotterCutLabelData = {
      pkg: plotterCut.package_code,
      name: plotterCut.package_name,
      mats: (plotterCut.materials || []).map((m) => ({
        n: m.material_name,
        l: m.layers,
        g: m.gsm,
      })),
      batch: plotterCut.batch_number || "N/A",
      roll: plotterCut.roll_id,
      cut_d: plotterCut.cut_date,
      op: plotterCut.operator_name || plotterCut.created_by_name || "N/A",
      wt: plotterCut.weight_grams,
      dim: plotterCut.dimensions,
      out_t: plotterCut.out_time_limit_minutes || 240, // Default 4 hours
      exp: plotterCut.expiration_date,
    };

    const displayCode = labelCode || `PKG-${plotterCut.package_code.split("-").pop()}`;

    return (
      <QRLabelBase
        ref={ref}
        shortCode={displayCode}
        size={size}
        title={plotterCut.package_code}
        subtitle={plotterCut.package_name}
      >
        <div style={{ fontSize: "inherit" }}>
          {/* Materials */}
          {size !== "small" && (
            <div style={{ marginBottom: "1mm" }}>
              <span style={{ color: "#4a5568", fontWeight: "600" }}>Materiales:</span>
              <MaterialList materials={labelData.mats} compact={size === "medium"} />
            </div>
          )}

          {/* Key info */}
          <LabelRow label="Lote" value={labelData.batch} highlight />
          {labelData.roll && <LabelRow label="Rollo" value={labelData.roll} />}
          <LabelRow
            label="Corte"
            value={format(new Date(labelData.cut_d), "dd/MM/yy", { locale: es })}
          />
          {labelData.wt && <LabelRow label="Peso" value={`${labelData.wt}g`} />}
          {labelData.dim && size !== "small" && <LabelRow label="Dim" value={labelData.dim} />}

          {/* Freezer warning */}
          <div
            style={{
              marginTop: "1mm",
              padding: "1mm",
              backgroundColor: "#fef3c7",
              border: "1px solid #f59e0b",
              borderRadius: "1mm",
              fontSize: size === "small" ? "5pt" : "7pt",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>Max fuera congelador:</span>
            <strong>{Math.floor(labelData.out_t / 60)}h {labelData.out_t % 60}m</strong>
          </div>

          {/* Operator */}
          {size !== "small" && (
            <div
              style={{
                marginTop: "1mm",
                fontSize: "6pt",
                color: "#718096",
                textAlign: "right",
              }}
            >
              Op: {labelData.op}
            </div>
          )}
        </div>
      </QRLabelBase>
    );
  }
);

PlotterCutLabel.displayName = "PlotterCutLabel";
